import { parseISO, addDays } from 'date-fns';
import apiClient from '../../../services/apiClient';
import { LicenseResponse } from './LicenseResponse';

export type TransformedLicense = LicenseResponse & {
  name?: string;
  expired?: boolean;
  isGridSubscription?: boolean;
  searchData: string[];
};

const isHyfiPreviewLicense = (license: LicenseResponse) =>
  !!(['005842385', '005842386', '005842387'].includes(license.item?.number) && license.endDate?.includes('2023-07-06'));

const hasExpired = (license: LicenseResponse) => {
  if (isHyfiPreviewLicense(license)) return true;
  return !!license.endDate && addDays(parseISO(license.endDate), 1).getTime() < Date.now();
};

const isGroupLicense = (license: LicenseResponse | TransformedLicense) => license.seats?.count.total !== 1;

export const categorizeLicenses = (
  acc: [TransformedLicense[], TransformedLicense[]],
  license: TransformedLicense
): [TransformedLicense[], TransformedLicense[]] => {
  const [groupLicenses, individualLicenses] = acc;
  if (isGroupLicense(license)) {
    groupLicenses.push(license);
  } else {
    individualLicenses.push(license);
  }
  return [groupLicenses, individualLicenses];
};

export const transformLicense = (license: LicenseResponse): TransformedLicense => {
  const transformed: TransformedLicense = {
    ...license,
    searchData: [license.item.title, license.owner.email, `${license.owner.firstName} ${license.owner.lastName}`],
  };

  if (transformed.item.title) transformed.name = transformed.item.title;
  if (transformed.startDate) transformed.startDate = transformed.startDate.split('T')[0];

  if (transformed.endDate) {
    transformed.endDate = transformed.endDate.split('T')[0];
    transformed.expired = hasExpired(license);
  }
  if (transformed.createdAt) transformed.createdAt = transformed.createdAt.split('T')[0];
  if (transformed.lastRenewalDate) transformed.lastRenewalDate = transformed.lastRenewalDate.split('T')[0];
  if (transformed.nextRenewalDate) transformed.nextRenewalDate = transformed.nextRenewalDate.split('T')[0];

  if (transformed.subscription) {
    if (transformed.subscription.period)
      transformed.subscription.period =
        transformed.subscription.period === 'Months' || transformed.subscription.period === 'Monthly'
          ? 'Monthly'
          : 'Yearly';

    if (transformed.subscription.lastRenewalDate)
      transformed.subscription.lastRenewalDate = transformed.subscription.lastRenewalDate.split('T')[0];
    if (transformed.subscription.nextRenewalDate)
      transformed.subscription.nextRenewalDate = transformed.subscription.nextRenewalDate.split('T')[0];
  }

  if (transformed.seats?.occupants) {
    transformed.seats.occupants.forEach(occupant => {
      occupant.profileImageUrl = `${apiClient.getConfig().baseURL}/v1/images/profile/${occupant.id}profile.png`;
    });
    transformed.searchData = transformed.searchData.concat(
      transformed.seats.occupants.flatMap(occupant => [occupant.email, `${occupant.firstName} ${occupant.lastName}`])
    );
  }

  if (
    transformed.seats &&
    transformed.seats.count &&
    transformed.seats.count.total === undefined &&
    transformed.seats.isLimited === false
  ) {
    transformed.seats.count.total = 'Unlimited';
    if (transformed.subscription) transformed.subscription.modifiable = false;
  }

  if (transformed.item) {
    const format = transformed.item.mediumFormat;
    transformed.isGridSubscription = format === undefined || format === '' || format === 'Learning Object';
    if (transformed.isGridSubscription === false && !!transformed.subscription) {
      transformed.subscription.modifiable = false;
    }
  }

  return transformed;
};
